var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { Button as SkapaButton, IconButton as SkapaIconButton } from '@ingka/button-webc/passive.js';
import CarouselLayoutEnum from '../../enums/CarouselLayoutEnum.js';
import CarouselControls from '../carousel-controls/CarouselControls.js';
import CarouselItem from '../carousel-item/CarouselItem.js';
import CarouselStyle from './CarouselStyle.js';
const SCROLLBAR_HEIGHT = 8;
const SWIPE_DISTANCE = 20;
/**
 * @deprecated This component has been deprecated, see Kompis page for more info.
 *
 * Carousel component.
 *
 * @event change Emits when selectedIndex has changed. e.g. when user is clicking on next-button or do a swipe gesture
 */
export default class Carousel extends KompisElement {
    constructor() {
        super(...arguments);
        // Visibility of navigation arrows
        this.showArrows = false;
        // Slides all visible items as one page
        this.slideVisibleItems = false;
        // Enables swiping with pointer
        this.pointerSwipe = false;
        // Show scrollbar representing pageIndex
        this.showScrollbar = false;
        // Enable to fit items.
        this.fitItems = false;
        // Applies padding in the bottom.
        this.paddingBottom = false;
        // Sets the button type for the navigation control buttons.
        this.buttonType = 'primary';
        // Sets the behaviour for the navigation control buttons.
        this.layout = CarouselLayoutEnum.default;
        // Set which slide index that should be active
        this.selectedIndex = 0;
        // Skip listing label
        this.skipListingLabel = null;
        // Label for the carousel to set as an aria-label for accessibility reasons
        this.ariaLabel = null;
        // Role description for the Carousel to set as an aria-roledescription for accessibility reasons
        this.ariaRoleDescription = null;
        // Label for the carousel control previous button to set as an aria-label for accessibility reasons
        this.ariaLabelCarouselControlsLeftArrow = null;
        // Label for the carousel control next button to set as an aria-label for accessibility reasons
        this.ariaLabelCarouselControlsRightArrow = null;
        // Set dragged state.
        this.isScrollbarHovered = false;
        this.hasFocus = false;
        this.resizeObserver = new ResizeObserver(() => {
            this.navigateToIndex(this.selectedIndex, 'auto');
            this.requestUpdate();
        });
        this.scrollTimeout = null;
        this.tabTimeout = null;
        this.arrowKeyTimeout = null;
        this.isUpdating = false;
        this.isDragging = false;
        this.isTabbing = false;
        this.isArrowKeyScrolling = false;
        this.isPointerDown = false;
        this.isSelectedIndexUpdated = false;
        this.startPosition = 0;
        this.startScrollLeft = 0;
        this.slideContainer = null;
        this.listeners = {
            pointerup: this.onPointerUp.bind(this),
            pointermove: this.onPointerMove.bind(this),
            pointerleave: this.onPointerUp.bind(this)
        };
    }
    /**
     * Renders the component.
     *
     * @returns Template.
     */
    render() {
        const direction = window.getComputedStyle(this).direction;
        return html `
			<style>
				${CarouselStyle.getStyle({
            hasFocus: this.hasFocus,
            isScrollbarHovered: this.isScrollbarHovered,
            fitItems: this.fitItems,
            itemCount: this.getChildren()?.length || 0,
            theme: this.theme,
            direction: direction,
            showScrollbar: this.showScrollbar,
            scrollBarHeight: SCROLLBAR_HEIGHT,
            layout: this.layout,
            paddingBottom: this.paddingBottom,
            container: this
        })}
			</style>
			<div class="slideContainerWrapper">
				<section
					class="slideContainer"
					@slotchange="${() => this.requestUpdate()}"
					@scroll="${this.onScroll.bind(this)}"
					@mousemove="${this.onMouseMove.bind(this)}"
					@mouseleave="${this.onMouseLeave.bind(this)}"
					@keydown="${(event) => this.onKeyDown(event)}"
					@pointerdown="${this.onPointerDown.bind(this)}"
					@focusin="${this.onFocusIn.bind(this)}"
					tabindex="-1"
					aria-roledescription="${this.ariaRoleDescription || ''}"
					aria-label="${this.ariaLabel || ''}"
				>
					${this.getSkipButton()}
					<slot></slot>
				</section>
			</div>
			${this.getControls()}
			<div
				class="skipListingFocusElement"
				@focusout="${(event) => {
            const target = event.currentTarget;
            if (target) {
                target.tabIndex = -1;
            }
        }}"
			></div>
		`;
    }
    async connectedCallback() {
        this.customElements.define('skapa-button', SkapaButton);
        this.customElements.define('skapa-icon-button', SkapaIconButton);
        this.customElements.define('kompis-carousel-controls', CarouselControls);
        this.customElements.define('kompis-carousel-item', CarouselItem);
        super.connectedCallback();
        this.resizeObserver.observe(this);
        document.body.addEventListener('pointerleave', this.listeners.pointerleave);
        document.body.addEventListener('pointerup', this.listeners.pointerup);
        document.body.addEventListener('pointermove', this.listeners.pointermove);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.resizeObserver.disconnect();
        if (this.scrollTimeout)
            clearTimeout(this.scrollTimeout);
        if (this.tabTimeout)
            clearTimeout(this.tabTimeout);
        this.scrollTimeout = null;
        this.isSelectedIndexUpdated = false;
        document.body.removeEventListener('pointerleave', this.listeners.pointerleave);
        document.body.removeEventListener('pointerup', this.listeners.pointerup);
        document.body.removeEventListener('pointermove', this.listeners.pointermove);
    }
    shouldUpdate(changedProperties) {
        if (this.getContainerElement()) {
            if (changedProperties.has('selectedIndex')) {
                if (!this.isUpdating) {
                    this.navigateToIndex(this.selectedIndex, !this.isSelectedIndexUpdated ? 'auto' : 'smooth');
                }
            }
            this.isUpdating = false;
            this.isSelectedIndexUpdated = true;
        }
        return true;
    }
    /**
     * @param child The child element.
     * @returns The percentage of the child that is visible in the viewport. Returns value between 0 and 100.
     */
    isChildVisible(child) {
        const scrollContainer = this.getContainerElement();
        if (!scrollContainer || !child) {
            return 0;
        }
        const x1 = scrollContainer.scrollLeft;
        const x2 = scrollContainer.scrollLeft + scrollContainer.offsetWidth;
        const y1 = child.offsetLeft;
        const y2 = child.offsetLeft + child.offsetWidth;
        const lt = Math.max(x1, y1); // The highest value of viewport left and child left
        const rt = Math.min(x2, y2); // The lowest value of viewport right and child right
        // Child is completely outside viewport
        if (rt - lt < 0) {
            return 0;
        }
        const percentageVisible = Math.round(100 * ((rt - lt) / child.offsetWidth));
        return percentageVisible;
    }
    /**
     * @returns If the left arrow should be visible.
     */
    showLeftArrow() {
        const firstChild = this.getChildren()[0];
        const thresholdPercentage = 85;
        return (this.showArrows && this.isChildVisible(firstChild) < thresholdPercentage && !this.isDragging);
    }
    /**
     * @returns If the right arrow should be visible.
     */
    showRightArrow() {
        const lastChild = this.getChildren()[this.getChildren().length - 1];
        const thresholdPercentage = 85;
        return (this.showArrows && this.isChildVisible(lastChild) < thresholdPercentage && !this.isDragging);
    }
    /**
     * @returns Get the arrows element.
     */
    getControls() {
        return html `
			<kompis-carousel-controls
				.theme="${this.theme}"
				.buttonType="${this.buttonType}"
				.layout="${this.layout}"
				.showLeftArrow="${this.showLeftArrow()}"
				.showRightArrow="${this.showRightArrow()}"
				.ariaLabelLeftArrow="${this.ariaLabelCarouselControlsLeftArrow}"
				.ariaLabelRightArrow="${this.ariaLabelCarouselControlsRightArrow}"
				@prev="${() => this.navigatePrev()}"
				@next="${() => this.navigateNext()}"
			></kompis-carousel-controls>
		`;
    }
    /**
     * Skip Button when entering a carousel through tabbing.
     *
     * @returns Template.
     */
    getSkipButton() {
        if (!this.skipListingLabel) {
            return null;
        }
        const primaryVariant = 'primary';
        return html `
			<skapa-button
				class="skipButton"
				variant="${primaryVariant}"
				inverse
				@focusout="${this.onSkipButtonBlur.bind(this)}"
				@focusin="${this.onSkipButtonFocus.bind(this)}"
				@click="${this.onSkipButtonClick.bind(this)}"
			>
				${this.skipListingLabel}
			</skapa-button>
		`;
    }
    /**
     * HTML event handler.
     */
    onSkipButtonBlur() {
        if (this.hasFocus) {
            this.hasFocus = false;
            this.requestUpdate();
        }
    }
    /**
     * HTML event handler.
     */
    onSkipButtonFocus() {
        if (!this.hasFocus) {
            this.hasFocus = true;
            this.requestUpdate();
        }
    }
    /**
     * HTML event handler.
     *
     * @param event Event.
     */
    onFocusIn(event) {
        if (!this.isTabbing) {
            return;
        }
        const path = event.composedPath();
        const children = this.getChildren();
        for (const node of path) {
            for (let i = 0; i < children.length; i++) {
                if (node === children[i] && i !== this.selectedIndex) {
                    this.dispatchEvent(new CustomEvent('change', { bubbles: true, detail: { selectedIndex: i } }));
                    return;
                }
            }
        }
    }
    /**
     * Handles scroll event.
     */
    onScroll() {
        if (this.scrollTimeout)
            clearTimeout(this.scrollTimeout);
        this.scrollTimeout = setTimeout(() => {
            if (this.scrollTimeout)
                clearTimeout(this.scrollTimeout);
            this.scrollTimeout = null;
            this.requestUpdate();
            if (this.isPointerDown) {
                this.onScroll();
            }
            else {
                const currentIndex = this.getCurrentIndex();
                if (currentIndex === null) {
                    return;
                }
                if (currentIndex !== this.selectedIndex) {
                    if (!this.showRightArrow()) {
                        this.isUpdating = true;
                    }
                    this.dispatchEvent(new CustomEvent('change', {
                        bubbles: true,
                        detail: { selectedIndex: currentIndex }
                    }));
                }
                else {
                    if (this.showRightArrow()) {
                        this.navigateToIndex(this.selectedIndex);
                    }
                }
            }
        }, 100);
    }
    /**
     * Event handler.
     */
    onSkipButtonClick() {
        const focusElement = this.shadowRoot?.querySelector('.skipListingFocusElement');
        focusElement.tabIndex = 0;
        focusElement.focus();
    }
    /**
     * Event handler.
     *
     * @param event Event.
     */
    onMouseMove(event) {
        if (!this.showScrollbar) {
            return;
        }
        const container = this.getContainerElement();
        if (event.offsetY >= container.offsetHeight - SCROLLBAR_HEIGHT) {
            if (!this.isScrollbarHovered) {
                this.isScrollbarHovered = true;
                this.requestUpdate();
            }
        }
        else if (this.isScrollbarHovered) {
            this.isScrollbarHovered = false;
            this.requestUpdate();
        }
    }
    /**
     * Event handler.
     */
    onMouseLeave() {
        if (!this.showScrollbar) {
            return;
        }
        if (this.isScrollbarHovered) {
            this.isScrollbarHovered = false;
            this.requestUpdate();
        }
    }
    /**
     * When key down.
     *
     * @param event Keyboard event.
     */
    onKeyDown(event) {
        switch (event.key) {
            case 'ArrowLeft':
            case 'ArrowRight':
                if (!this.isArrowKeyScrolling) {
                    this.isArrowKeyScrolling = true;
                    if (this.arrowKeyTimeout)
                        clearTimeout(this.arrowKeyTimeout);
                    this.arrowKeyTimeout = setTimeout(() => (this.isArrowKeyScrolling = false), 400);
                    const direction = window.getComputedStyle(this).direction;
                    event.preventDefault();
                    if (event.key === 'ArrowLeft') {
                        if (direction !== 'rtl') {
                            this.navigatePrev();
                        }
                        else {
                            this.navigateNext();
                        }
                    }
                    else {
                        if (direction !== 'rtl') {
                            this.navigateNext();
                        }
                        else {
                            this.navigatePrev();
                        }
                    }
                }
                break;
            case 'Tab':
                this.isTabbing = true;
                if (this.tabTimeout)
                    clearTimeout(this.tabTimeout);
                this.tabTimeout = setTimeout(() => (this.isTabbing = false));
                break;
        }
    }
    /**
     * @returns Item width.
     */
    getItemWidth() {
        return this.getChildren()[0].offsetWidth;
    }
    /**
     * @returns Returns container element.
     */
    getContainerElement() {
        return this.shadowRoot?.querySelector('.slideContainer');
    }
    /**
     * @returns Children.
     */
    getChildren() {
        if (this.children.length === 1 && this.children[0].tagName === 'SLOT') {
            return Array.from(this.getRootNode().host.children);
        }
        return Array.from(this.children);
    }
    /**
     * @returns Current index.
     */
    getCurrentIndex() {
        const scrollContainer = this.getContainerElement();
        const children = this.getChildren();
        if (!children.length) {
            return null;
        }
        const offsetLeft = children[0].offsetLeft;
        for (let index = 0; index < children.length; index++) {
            const child = children[index];
            if (Math.abs(child.offsetLeft - offsetLeft) + child.offsetWidth / 2 >
                Math.abs(scrollContainer.scrollLeft)) {
                return index;
            }
        }
        return this.getChildren().length - 1;
    }
    /**
     * Scrolls left.
     */
    navigatePrev() {
        const nextIndex = this.selectedIndex - this.getItemsPerPage();
        const selectedIndex = nextIndex > 0 ? nextIndex : 0;
        this.navigateToIndex(selectedIndex);
    }
    /**
     * Scrolls right.
     */
    navigateNext() {
        const nextIndex = this.selectedIndex + this.getItemsPerPage();
        const children = this.getChildren();
        const selectedIndex = nextIndex < children.length ? nextIndex : children.length - 1;
        this.navigateToIndex(selectedIndex);
    }
    /**
     * @param index Index.
     * @param behavior Behaviour.
     */
    navigateToIndex(index, behavior = 'smooth') {
        const scrollContainer = this.getContainerElement();
        if (!scrollContainer) {
            return;
        }
        const children = this.getChildren();
        const child = children[index];
        if (!child) {
            return;
        }
        const offsetLeft = children[0].offsetLeft;
        const scrollTo = child.offsetLeft - offsetLeft;
        if (scrollContainer.scrollLeft != scrollTo) {
            scrollContainer.scrollTo({
                left: scrollTo,
                behavior
            });
        }
    }
    /**
     * @returns Items per page.
     */
    getItemsPerPage() {
        if (this.slideVisibleItems) {
            return Math.floor(this.offsetWidth / this.getItemWidth());
        }
        return 1;
    }
    /**
     * Handles pointer down event.
     *
     * @param event MouseEvent.
     */
    onPointerDown(event) {
        this.isPointerDown = true;
        if (!this.pointerSwipe) {
            return;
        }
        event.preventDefault();
        this.slideContainer = event.currentTarget;
        this.startPosition = this.slideContainer.scrollLeft + event.clientX;
        this.startScrollLeft = this.slideContainer.scrollLeft;
        this.isDragging = true;
        this.update();
    }
    /**
     * Handles pointer move event and emits event if item was dragged.
     *
     * @param event MouseEvent.
     */
    onPointerMove(event) {
        if (!this.isDragging || !this.pointerSwipe) {
            return;
        }
        event.preventDefault();
        this.slideContainer.scrollLeft = this.startPosition - event.clientX;
    }
    /**
     * Handles pointer up event.
     */
    onPointerUp() {
        this.isPointerDown = false;
        if (!this.isDragging || !this.pointerSwipe) {
            return;
        }
        this.isDragging = false;
        const rtl = this.slideContainer.scrollLeft < 0 ? -1 : 1;
        const distance = this.slideContainer.scrollLeft - this.startScrollLeft * rtl;
        if (Math.abs(distance) > SWIPE_DISTANCE) {
            if (distance < 0) {
                this.navigatePrev();
            }
            else {
                this.navigateNext();
            }
        }
        this.startPosition = 0;
        this.startScrollLeft = 0;
        this.requestUpdate();
    }
}
__decorate([
    property({ type: Boolean })
], Carousel.prototype, "showArrows", void 0);
__decorate([
    property({ type: Boolean })
], Carousel.prototype, "slideVisibleItems", void 0);
__decorate([
    property({ type: Boolean })
], Carousel.prototype, "pointerSwipe", void 0);
__decorate([
    property({ type: Boolean })
], Carousel.prototype, "showScrollbar", void 0);
__decorate([
    property({ type: Boolean })
], Carousel.prototype, "fitItems", void 0);
__decorate([
    property({ type: Boolean })
], Carousel.prototype, "paddingBottom", void 0);
__decorate([
    property({ type: String })
], Carousel.prototype, "buttonType", void 0);
__decorate([
    property({ type: Object, required: true })
], Carousel.prototype, "theme", void 0);
__decorate([
    property({ type: String })
], Carousel.prototype, "layout", void 0);
__decorate([
    property({ type: Number, synchronous: true })
], Carousel.prototype, "selectedIndex", void 0);
__decorate([
    property({ type: String })
], Carousel.prototype, "skipListingLabel", void 0);
__decorate([
    property({ type: String })
], Carousel.prototype, "ariaLabel", void 0);
__decorate([
    property({ type: String })
], Carousel.prototype, "ariaRoleDescription", void 0);
__decorate([
    property({ type: String })
], Carousel.prototype, "ariaLabelCarouselControlsLeftArrow", void 0);
__decorate([
    property({ type: String })
], Carousel.prototype, "ariaLabelCarouselControlsRightArrow", void 0);
//# sourceMappingURL=Carousel.js.map